<template>
  <div class="h-full overflow-scroll">
    <BaseTable
      table-id="salesDoc-details-table"
      :headers="headers"
      :list="tableLines"
      :is-filter-button-hidden="true"
      :selected-row-id="editedLine?.id"
      is-saving-layout-hidden
      is-search-hidden
      is-refresh-button-hidden
      is-download-button-hidden
      hide-grouping
      :is-add-new-hidden="mode === 'view'"
      :edit-mode="mode === 'edit'"
      disable-multiselect
      @add-new="addNewLine">
      <template #controls>
        <BaseControlsButtonIcon
          v-if="mode === 'edit'"
          :tooltip-text="$t('common.tooltip.filterMaterialsByGroup')"
          @click="openFiltersModal">
          <IconFilters class="size-4" />
        </BaseControlsButtonIcon>
      </template>
      <template #top>
        <div class="flex flex-wrap gap-1.5 my-2 justify-center">
          <div
            v-for="(item, index) in visibleTags"
            :key="index"
            class="inline-flex gap-x-0.5 items-center px-2 py-1 text-xs font-medium text-gray-600 rounded-full bg-theme-100">
            {{ item.name }}

            <IconXmark
              class="w-3.5 h-3.5 ml-auto cursor-pointer"
              @click="handleSelection(item)"></IconXmark>
          </div>
          <button
            v-if="activeFilters && activeFilters.length > maxVisibleTags"
            type="button"
            class="mx-1 text-xs hover:text-theme-700"
            @click="toggleVisibleTags">
            {{
              showMore
                ? $t('common.actions.showLess')
                : `${$t('common.actions.showMore')} (${remainingTags})`
            }}
          </button>
        </div>
      </template>
      <template v-if="tableLines" #custom="{ item, header }">
        <div class="flex items-center w-full px-1">
          <BaseTableCellSearchbox
            v-if="header === 'materialCode'"
            v-model="item.materialCode"
            :mode="mode"
            :lineFocusInput="newDocumentLine?.id === item.id && header === 'materialCode'"
            :error="getRowFieldError(item.id, 'materialAlterCodeId')"
            @update-search="onUpdateMaterialAlterCodesSearch(item.materialCode, item)"
            @clear-selection="clearItem(item)" />

          <BaseTableCellSearchbox
            v-if="header === 'materialName'"
            v-model="item.materialName"
            :mode="mode"
            :error="getRowFieldError(item.id, 'materialAlterCodeId')"
            @update-search="onUpdateMaterialAlterCodesSearch(item.materialName, item)"
            @clear-selection="clearItem(item)" />

          <BaseSelectDropdown
            v-if="header === 'warehouse.name'"
            class="w-full"
            name="warehouseDropdown"
            size="sm"
            flat
            :items="dropdownWarehouses"
            :selected-item="{ value: item.warehouseId, name: item.warehouse?.name }"
            @update:selected="handleWarehouseSelection($event, item)"
            :mode="mode" />

          <BaseInput
            v-if="header === 'unitPrice' || header === 'grossTotal'"
            type="number"
            price
            color="base"
            icon="right"
            class="h-8 bg-white rounded-md"
            v-model="item[header]"
            :disabled="isInputDisabled(header, item)"
            @update:model-value="inputUpdated(header, item)"
            show-error-as-tooltip
            :error="getRowFieldError(item.id, header)">
            <template #icon-right>
              <div
                class="flex items-center uppercase font-light px-1 h-[30px] text-gray-400 bg-slate-200 rounded-r-md border-l border-gray-300 text-xs">
                {{ '€' }}
              </div>
            </template>
          </BaseInput>
          <BaseToggle
            v-if="header === 'exists' || header === 'notExists'"
            class="flex flex-col justify-center text-sm"
            :name="`detail-${item.id}-${header}`"
            v-model="item[header]"
            @update:model-value="inputUpdated(header, item)" />
          <BaseInput
            v-if="
              header === 'quantity' ||
              header === 'quantity2' ||
              header === 'quantity3' ||
              header === 'totalQuantity' ||
              header === 'percentageDiscount' ||
              header === 'unitDiscount' ||
              header === 'netTotal' ||
              header === 'lineVatTotal' ||
              header === 'vatFactorValue'
            "
            type="number"
            color="base"
            :min="0"
            :price="
              header === 'unitDiscount' ||
              header === 'netTotal' ||
              header === 'lineVatTotal' ||
              header === 'vatFactorValue'
            "
            :text-right="
              header === 'quantity' ||
              header === 'quantity2' ||
              header === 'quantity3' ||
              header === 'totalQuantity' ||
              header === 'percentageDiscount'
            "
            class="h-8 bg-white rounded-md"
            v-model="item[header]"
            :disabled="isInputDisabled(header, item)"
            :readonly="
              (header === 'percentageDiscount' || header === 'unitDiscount') &&
              item.allowDiscount === false
            "
            @update:model-value="inputUpdated(header, item)"
            show-error-as-tooltip
            :lineFocusInput="
              header === 'quantity' &&
              editedLine?.id === item.id &&
              !!editedLine?.materialAlterCodeId
            "
            :error="getRowFieldError(item.id, header)" />
          <button
            class="p-1"
            @click="getBalanceByMaterialId(item)"
            v-if="header === 'quantity' && item.materialCode && item.materialName">
            <IconSearch class="text-gray-400 cursor-pointer size-4 hover:text-theme-600" />
          </button>
        </div>
      </template>
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1 w-14">
          <button
            v-if="!isNewLine(item)"
            class="flex items-center justify-center p-[1px] rounded-full bg-slate-200 size-4"
            @click="onLineInfoClick(item.materialId, item.materialCode, item.materialName)">
            <IconInfo class="rounded-full hover:text-theme-600 hove:bg-theme-100"></IconInfo>
          </button>
          <button
            class="p-1"
            @click="saveDocumentLine(item)"
            v-if="item.isModified && mode === 'edit'">
            <IconSave class="cursor-pointer size-4 hover:text-theme-600" />
          </button>
          <button
            class="p-1"
            @click="discardDocumentLine(item)"
            v-if="item.isModified && !isNewLine(item) && mode === 'edit'">
            <IconXmark class="cursor-pointer size-4 hover:text-red-600" />
          </button>
          <button
            class="p-1"
            @click="deleteLinePrompt(item)"
            v-if="mode === 'edit' && (isNewLine(item) || !item.isModified)">
            <IconTrashOutline class="cursor-pointer size-4 hover:text-red-600" />
          </button>
        </div>
      </template>
    </BaseTable>
  </div>
  <MaterialAlterCodesSearchResultsModal
    v-if="alterCodesModalVisible"
    @cancel="onMaterialAlterCodesSearchCancel"
    @select="onMaterialAlterCodeSelected"
    @show-modal="getBalanceByMaterialId"
    :search-results="materialAlterCodesList" />

  <InfoModal
    v-if="showBOMaterialBalanceModal"
    @primary-button-clicked="showBOMaterialBalanceModal = false">
    <template #content>
      <div v-html="htmlContent"></div>
    </template>
  </InfoModal>
  <BaseModalConfirm
    v-if="showDeleteConfirmPrompt"
    @confirm="deleteLine"
    @cancel="showDeleteConfirmPrompt = false" />

  <SalesDocumentsDetailsFiltersModal
    v-if="isFiltersModalOpen"
    :active-filters="activeFilters"
    @cancel="closeFiltersModal"
    @save="onFiltersSelected"
    @reset-filters="removeAllFilters" />
</template>
<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { useDebounceFn } from '@vueuse/core';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseTableCellSearchbox from '@/components/common/BaseTableCellSearchbox.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import MaterialAlterCodesSearchResultsModal from '@/components/transactions/salesDocuments/MaterialAlterCodesSearchResultsModal.vue';
import IconSave from '@/assets/icons/IconSave.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import IconXmark from '@/assets/icons/IconXmark.vue';
import IconInfo from '@/assets/icons/IconInfo.vue';
import IconFilters from '@/assets/icons/IconFilters.vue';
import { deepCopy } from '@/utils/data';
import { getCustomerHtmlTabs } from '@/services/api/traders';
import type { MaterialAlterCodeForSalesDocument } from '@/types/Materials';
import { type DocumentDetail } from '@/types/Documents';
import { RecordMode, type SelectDropdownItem } from '@/types/Base';
import { useLookupsStore } from '@/stores/lookups';
import { useSalesDocumentsStore } from '@/stores/salesDocuments';
import { useToastStore } from '@/stores/toast';
import IconSearch from '@/assets/icons/IconSearch.vue';
import InfoModal from '@/components/common/InfoModal.vue';
import { helpers, maxValue, minValue, required } from '@vuelidate/validators';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseControlsButtonIcon from '@/components/common/BaseControlsButtonIcon.vue';
import SalesDocumentsDetailsFiltersModal from './SalesDocumentsDetailsFiltersModal.vue';
import { getMaterialById } from '@/services/api/materials';
import type { FilterGroupItems } from '@/types/Groups';

const emit = defineEmits(['validateHeader', 'materialInfo']);

// Modal state management
const isFiltersModalOpen = ref(false);
const toastStore = useToastStore();
const { t: $t } = useI18n();

const { getWarehousesLookup } = useLookupsStore();
const { warehouses, dropdownWarehouses } = storeToRefs(useLookupsStore());
const { clearDetails, modifiedSalesDocument, docDetailLines, activeFilters } =
  storeToRefs(useSalesDocumentsStore());
const {
  searchMaterialAlterCodes,
  getMaterialPropertiesFromCustomerPricingPolicy,
  getMaterialDiscountsFromCustomerPricingPolicy,
  getMaterialVatFactor,
  updateDocLines,
  priceOrQuantityChange,
  discountValueChange,
  discountPercentageChange,
  grossTotalChange,
  netTotalChange,
  initNewDocumentLine,
  saveDocumentLineInStore,
  priceZeroRule,
  discountNotMoreThanUnitPrice,
  docLineQuantityRule,
  docLineUnitPriceRule,
  materialActiveCatalogRule,
  updateExistsNotExistsFlags,
  setActiveFilters,
  removeFilter,
  clearAllFilters,
  processSiblingMaterialAlterCodes,
  initializeDynamicQuantities,
} = useSalesDocumentsStore();

const props = defineProps<{
  mode: 'view' | 'edit';
}>();

function isNewLine(item: DocumentDetail): boolean {
  if (!newLineAdded.value) return false;
  return newDocumentLine.value?.id === item.id;
}

// Dynamic headers with active property based on DocumentType's totalMeasurementUnits
const headers = computed(() => {
  const totalUnits = Math.min(
    modifiedSalesDocument.value?.documentHeader?.documentType?.totalMeasurementUnits || 1,
    3
  );

  return [
    { value: 'materialCode', text: $t('baseObject.code'), type: 'custom', active: true },
    {
      value: 'materialName',
      text: $t('salesDocuments.description'),
      type: 'custom',
      active: true,
    },
    {
      value: 'warehouse.name',
      text: $t('salesDocuments.headers.warehouse'),
      type: 'custom',
      active: true,
    },
    { value: 'quantity', text: $t('salesDocuments.quantity'), type: 'custom', active: true },
    {
      value: 'quantity2',
      text: $t('salesDocuments.quantity') + ' 2',
      type: 'custom',
      active: totalUnits >= 2,
    },
    {
      value: 'quantity3',
      text: $t('salesDocuments.quantity') + ' 3',
      type: 'custom',
      active: totalUnits >= 3,
    },
    {
      value: 'totalQuantity',
      text: $t('salesDocuments.totalQuantity'),
      type: 'custom',
      editable: false,
      active: totalUnits > 1,
    },
    { value: 'unitPrice', text: $t('salesDocuments.unitPrice'), type: 'custom', active: true },
    {
      value: 'unitDiscount',
      text: $t('salesDocuments.unitDiscount'),
      type: 'custom',
      active: true,
    },
    {
      value: 'percentageDiscount',
      text: $t('salesDocuments.unitDiscountPercentage'),
      type: 'custom',
      active: true,
    },
    {
      value: 'exists',
      text: $t('salesDocuments.exists'),
      type: 'check',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'notExists',
      text: $t('salesDocuments.notExists'),
      type: 'check',
      active: true,
      hasInlineEdit: true,
    },
    {
      value: 'netTotal',
      text: $t('salesDocuments.netTotal'),
      type: 'custom',
      editable: false,
      active: true,
    },
    {
      value: 'lineVatTotal',
      text: $t('salesDocuments.vat'),
      type: 'custom',
      editable: false,
      active: true,
    },
    {
      value: 'vatFactorValue',
      text: $t('salesDocuments.vatFactor'),
      type: 'custom',
      editable: false,
      active: true,
    },
    {
      value: 'grossTotal',
      text: $t('salesDocuments.headers.totalGross'),
      type: 'custom',
      editable: false,
      active: true,
    },
    { value: 'actions', text: '', type: 'custom', active: true },
  ];
});

const materialAlterCodesList = ref<MaterialAlterCodeForSalesDocument[]>([]);
const alterCodesModalVisible = ref(false);
const showDeleteConfirmPrompt = ref(false);
const newDocumentLine = ref<DocumentDetail | null>(null);
const newLineAdded = ref(false);
const editedLine = ref<DocumentDetail>();
const showBOMaterialBalanceModal = ref(false);
const htmlContent = ref('');

const tableLines = computed(() => {
  return docDetailLines.value.filter((l) => l.recordMode != RecordMode.Deleted);
});

function isInputDisabled(header: string, item: DocumentDetail) {
  switch (header) {
    case 'vatFactorValue':
    case 'lineVatTotal':
    case 'totalQuantity':
      return true;
    case 'quantity':
      return props.mode === 'view';
    case 'quantity2':
      return (
        props.mode === 'view' ||
        !item.measurementUnit2Id ||
        item.measurementUnit2Id === '00000000-0000-0000-0000-000000000000'
      );
    case 'quantity3':
      return (
        props.mode === 'view' ||
        !item.measurementUnit3Id ||
        item.measurementUnit3Id === '00000000-0000-0000-0000-000000000000'
      );
    case 'netTotal':
    case 'grossTotal':
      return props.mode === 'view';
    case 'percentageDiscount':
    case 'unitDiscount':
      return props.mode === 'view' || !item.allowDiscount;
    case 'unitPrice': {
      const typeAllowManualPrices =
        !!modifiedSalesDocument.value?.documentHeader.documentType?.allowManualPrices;
      return (
        props.mode === 'view' ||
        !typeAllowManualPrices ||
        !item.material ||
        !item.material.allowManualPrice
      );
    }
  }
}

function addNewLine() {
  if (newLineAdded.value) return;
  if (
    !modifiedSalesDocument.value?.customerId ||
    !modifiedSalesDocument.value.documentHeader.billingAddressId ||
    !modifiedSalesDocument.value.documentHeader.documentTypeId
  ) {
    emit('validateHeader');
    toastStore.attention({
      text: $t('messages.errors.salesDocumentHeaderIncomplete'),
      hideTitle: true,
      timeout: 5000,
    });
    return;
  }
  newLineAdded.value = true;
  newDocumentLine.value = initNewDocumentLine();
  docDetailLines.value = [newDocumentLine.value, ...docDetailLines.value];
  editedLine.value = newDocumentLine.value;
}

onMounted(async () => {
  await getWarehousesLookup();
  docDetailLines.value = deepCopy<DocumentDetail[]>(
    modifiedSalesDocument.value?.documentHeader.documentDetails?.filter((d) => !d.deleted) ?? []
  );

  // Initialize measurement unit fields for all document details on component mount
  if (docDetailLines.value.length > 0) {
    docDetailLines.value.forEach((detail) => {
      initializeDynamicQuantities(
        detail,
        modifiedSalesDocument.value?.documentHeader?.documentType
      );
    });
  }
});

watch(
  () => modifiedSalesDocument.value?.documentHeader.documentDetails,
  async (newDetails) => {
    if (!newDetails) return;
    const newDetailsCopy = deepCopy<DocumentDetail[]>(newDetails);
    if (!clearDetails.value) {
      // when on edit mode I save one line but there are more that have been modified, do not overwrite them.
      const localUnsaved = docDetailLines.value.filter(
        (l) => l.isModified && l.id !== editedLine.value?.id
      );
      const localUnsavedIds = localUnsaved.map((x) => x.id);

      // Merge unsaved changes into the new details
      for (const newDetail of newDetailsCopy) {
        if (localUnsavedIds.includes(newDetail.id)) {
          const index = newDetailsCopy.findIndex((x) => x.id === newDetail.id);
          newDetailsCopy[index] = {
            ...newDetailsCopy[index],
            ...localUnsaved.find((x) => x.id === newDetail.id),
          };
        }
      }
    }
    docDetailLines.value = deepCopy<DocumentDetail[]>(
      newDetailsCopy.filter((d) => !d.deleted) || []
    );
    // Keep the new document line, if applicable
    if (newDocumentLine.value)
      docDetailLines.value = [newDocumentLine.value, ...docDetailLines.value];

    clearDetails.value = false;

    await nextTick();
    t$.value.lines.$touch();
  },
  { deep: true }
);

async function onUpdateMaterialAlterCodesSearch(searchText: string, item: DocumentDetail) {
  editedLine.value = item;
  const results = await searchMaterialAlterCodes(
    searchText,
    undefined,
    activeFilters.value.map((f) => f.id)
  );
  if (results.length > 1) {
    materialAlterCodesList.value = results;
    alterCodesModalVisible.value = true;
  } else if (results.length === 1) await onMaterialAlterCodeSelected(results[0]);
  else {
    toastStore.attention({
      text: $t('messages.noResultsFor', { query: searchText }),
      hideTitle: true,
    });
    if (editedLine.value.materialAlterCode?.alterCode) {
      editedLine.value.materialName = editedLine.value.materialAlterCode.alterCode.name;
      editedLine.value.materialCode = editedLine.value.materialAlterCode.alterCode.code;
    }
  }
}

async function onMaterialAlterCodeSelected(item: MaterialAlterCodeForSalesDocument) {
  if (
    editedLine.value &&
    item.id &&
    item.alterCode &&
    item.alterCode.name &&
    item.alterCode.code &&
    item.materialId
  ) {
    editedLine.value.materialAlterCode = item;
    editedLine.value.materialAlterCodeId = item.id;
    editedLine.value.materialCode = item.alterCode.code;
    editedLine.value.materialName = item.alterCode.name;
    editedLine.value.materialId = item.materialId;

    // Process sibling MaterialAlterCodes for measurement unit mapping
    if (item.allMaterialAlterCodes && item.allMaterialAlterCodes.length > 0 && editedLine.value) {
      editedLine.value = processSiblingMaterialAlterCodes(
        editedLine.value,
        item,
        Math.min(
          modifiedSalesDocument.value?.documentHeader?.documentType?.totalMeasurementUnits || 1,
          3
        )
      );
    } else if (editedLine.value) {
      // Fallback to original logic if no sibling data
      editedLine.value.measurementUnit1Id = item.measurementUnitId || '';
    }

    editedLine.value = await getMaterialPropertiesFromCustomerPricingPolicy(editedLine.value);
    if (editedLine.value)
      editedLine.value = await getMaterialDiscountsFromCustomerPricingPolicy(editedLine.value);

    if (editedLine.value) editedLine.value = await getMaterialVatFactor(editedLine.value);
    if (editedLine.value)
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
  }
  alterCodesModalVisible.value = false;
}

function onMaterialAlterCodesSearchCancel() {
  if (editedLine.value && editedLine.value.materialAlterCode?.alterCode) {
    editedLine.value.materialName = editedLine.value.materialAlterCode.alterCode.name;
    editedLine.value.materialCode = editedLine.value.materialAlterCode.alterCode.code;
  }
  alterCodesModalVisible.value = false;
}

function inputUpdated(
  header:
    | 'quantity'
    | 'quantity2'
    | 'quantity3'
    | 'totalQuantity'
    | 'unitPrice'
    | 'percentageDiscount'
    | 'unitDiscount'
    | 'exists'
    | 'notExists'
    | 'grossTotal'
    | 'netTotal'
    | 'lineVatTotal'
    | 'vatFactorValue',
  item: DocumentDetail
) {
  editedLine.value = item;
  switch (header) {
    case 'unitPrice':
      editedLine.value = priceOrQuantityChange(editedLine.value, 'unitPrice');
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
      break;
    case 'quantity':
    case 'quantity2':
    case 'quantity3':
      editedLine.value = priceOrQuantityChange(editedLine.value, 'quantity');
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
      break;
    case 'unitDiscount':
      editedLine.value = discountValueChange(
        editedLine.value,
        getRowFieldError(editedLine.value.id, 'unitDiscount')
      );
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
      break;
    case 'percentageDiscount':
      editedLine.value = discountPercentageChange(
        editedLine.value,
        getRowFieldError(editedLine.value.id, 'percentageDiscount')
      );
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
      break;
    case 'exists':
    case 'notExists':
      editedLine.value = updateExistsNotExistsFlags(
        editedLine.value,
        header as 'exists' | 'notExists'
      );
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
      break;
    case 'grossTotal':
      debounceGrossTotalChange();
      break;
    case 'netTotal':
      debounceNetTotalChange();
      break;
    case 'lineVatTotal':
      break;
    case 'vatFactorValue':
      break;
    case 'totalQuantity':
      break;
  }
}

const debounceGrossTotalChange = useDebounceFn(
  () => {
    if (editedLine.value) {
      editedLine.value = grossTotalChange(editedLine.value);
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
    }
  },
  700,
  { maxWait: 1200 }
);

const debounceNetTotalChange = useDebounceFn(
  () => {
    if (editedLine.value) {
      editedLine.value = netTotalChange(editedLine.value);
      docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
    }
  },
  700,
  { maxWait: 1200 }
);

function clearItem(line: DocumentDetail) {
  editedLine.value = line;
  if (editedLine.value) {
    editedLine.value.materialCode = '';
    editedLine.value.materialName = '';
    editedLine.value.materialAlterCodeId = '';
    editedLine.value.unitPrice = 0;
    docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
  }
}

function handleWarehouseSelection(event: SelectDropdownItem, item: DocumentDetail) {
  editedLine.value = item;
  if (editedLine.value) {
    const selectedWarehouse = warehouses.value.find((x) => x.id === event.value);
    if (selectedWarehouse) {
      editedLine.value.warehouseId = selectedWarehouse.id;
      editedLine.value.warehouse = selectedWarehouse;
    }
    docDetailLines.value = updateDocLines(editedLine.value, docDetailLines.value);
  }
}

async function getBalanceByMaterialId(line: DocumentDetail) {
  htmlContent.value = '';
  if (!line.materialId) {
    toastStore.attention({
      text: $t('messages.noResultsText'),
      hideTitle: true,
    });
    return;
  }
  const material = await getMaterialById(line.materialId);
  if (material && material.boid) {
    const htmlTabs = await getCustomerHtmlTabs({
      enumValue: 4,
      materialBoid: material.boid,
    });
    if (htmlTabs) {
      htmlContent.value = htmlTabs;
      showBOMaterialBalanceModal.value = true;
    }
  }
}

function saveDocumentLine(line: DocumentDetail) {
  const rowIndex = tableLines.value.findIndex((row) => row.id === line.id);
  t$.value.$touch();
  if (t$.value.$invalid) {
    const rowErrors = t$.value.$errors[0]?.$response?.$errors[rowIndex];
    const hasErrors = rowErrors
      ? Object.values(rowErrors).some((value) => Array.isArray(value) && value.length > 0)
      : false;
    if (hasErrors) return;
  }
  editedLine.value = line;
  if (editedLine.value && editedLine.value.id === line.id) editedLine.value.isModified = false; // updates the memory object so as to control the toast notification

  saveDocumentLineInStore(line);
  const index = docDetailLines.value.findIndex((l) => l.id === line.id);
  if (index === -1) docDetailLines.value = [line, ...docDetailLines.value];
  else docDetailLines.value[index] = { ...docDetailLines.value[index], ...line };

  if (newDocumentLine.value && newDocumentLine.value.id === line.id) {
    newDocumentLine.value = null;
    newLineAdded.value = false;
  }
}

function discardDocumentLine(line: DocumentDetail) {
  line.isModified = false;
  const index = docDetailLines.value.findIndex((x) => x.id === line.id);
  const lineBeforeChange = modifiedSalesDocument.value?.documentHeader.documentDetails?.find(
    (x) => x.id == line.id
  );
  if (index == undefined || index < 0 || !lineBeforeChange) return;
  docDetailLines.value[index] = {
    ...docDetailLines.value[index],
    ...lineBeforeChange,
  };
  editedLine.value = docDetailLines.value[index];
}

function deleteLinePrompt(item: DocumentDetail) {
  editedLine.value = item;
  showDeleteConfirmPrompt.value = true;
}

function deleteLine() {
  showDeleteConfirmPrompt.value = false;

  if (editedLine.value) {
    if (!!newLineAdded.value && newDocumentLine.value?.id == editedLine.value.id) {
      const index = docDetailLines.value.findIndex((x) => x.id === newDocumentLine.value?.id);
      if (index == -1) return;
      docDetailLines.value.splice(index, 1);
      newLineAdded.value = false;
      newDocumentLine.value = null;
    } else {
      if (editedLine.value.recordMode === RecordMode.Added) {
        saveDocumentLineInStore(editedLine.value, true);
      } else {
        editedLine.value.recordMode = RecordMode.Deleted;
        saveDocumentLine(editedLine.value);
      }
    }
  }
}

function onLineInfoClick(materialId: string, code: string, name: string) {
  emit('materialInfo', { materialId, code, name });
}

watch(
  () => editedLine.value,
  (newLine, oldLine) => {
    // If switching to a new line while the previous line was modified, prompt the user to save changes
    if (newLine?.id != oldLine?.id && oldLine?.isModified)
      toastStore.attention({
        text: $t('messages.saveLineChangesPrompt'),
        hideTitle: true,
      });

    // If switching from the new document line, restore it as the edited line
    if (
      newDocumentLine.value &&
      oldLine &&
      newLine &&
      newLine.id != oldLine.id &&
      oldLine.id === newDocumentLine.value.id
    )
      newDocumentLine.value = oldLine;
  },
  { deep: true }
);

const tableModel = computed(() => ({
  lines: docDetailLines.value,
}));

const rulesTable = computed(() => ({
  lines: {
    $each: helpers.forEach({
      materialAlterCodeId: {
        required: helpers.withMessage('Material selection is required', required),
        materialActiveCatalogRule,
      },
      totalQuantity: { docLineQuantityRule },
      unitPrice: { docLineUnitPriceRule },
      grossTotal: { priceZeroRule },
      unitDiscount: {
        discountNotMoreThanUnitPrice,
      },
      percentageDiscount: {
        minValue: minValue(0),
        maxValue: maxValue(100),
      },
    }),
  },
}));

const t$ = useVuelidate(rulesTable, tableModel);

function getRowFieldError(rowId: string, field: string): string {
  const rowIndex = tableLines.value.findIndex((row) => row.id === rowId);
  const tableErrors = t$.value.$errors[0]?.$response?.$errors;
  if (!tableErrors || !tableErrors[rowIndex]) return '';
  const fieldErrors = tableErrors[rowIndex][field];
  return fieldErrors && fieldErrors.length > 0 ? fieldErrors[0].$message : '';
}

function openFiltersModal() {
  isFiltersModalOpen.value = true;
}

function closeFiltersModal() {
  isFiltersModalOpen.value = false;
}

function onFiltersSelected(selectedFilters: FilterGroupItems[]) {
  setActiveFilters(selectedFilters);
  closeFiltersModal();
}

const visibleTagsNum = ref(20);

const maxVisibleTags = ref(20);

const showMore = ref(false);

const visibleTags = computed(() => {
  if (!activeFilters.value) return [];
  return activeFilters.value.slice(0, visibleTagsNum.value);
});

const remainingTags = computed(() => {
  if (!activeFilters.value || activeFilters.value.length <= visibleTagsNum.value) return 0;
  return activeFilters.value.length - visibleTagsNum.value;
});

function toggleVisibleTags() {
  if (showMore.value) {
    showMore.value = false;
    visibleTagsNum.value = 20;
  } else {
    showMore.value = true;
    visibleTagsNum.value = activeFilters.value?.length || 0;
  }
}

function handleSelection(item: FilterGroupItems) {
  removeFilter(item.id);
}

function removeAllFilters() {
  clearAllFilters();
  closeFiltersModal();
}
</script>
